/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {ParseSourceSpan} from '../parse_util';

export interface Node {
  sourceSpan: ParseSourceSpan;
  visit(visitor: Visitor, context: any): any;
}

export class Text implements Node {
/**
 * @param {?} value
 * @param {?} sourceSpan
 */
constructor(public value: string,
public sourceSpan: ParseSourceSpan) {}
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: Visitor, context: any): any { return visitor.visitText(this, context); }
}

function Text_tsickle_Closure_declarations() {
 /** @type {?} */
Text.prototype.value;
 /** @type {?} */
Text.prototype.sourceSpan;
}


export class Expansion implements Node {
/**
 * @param {?} switchValue
 * @param {?} type
 * @param {?} cases
 * @param {?} sourceSpan
 * @param {?} switchValueSourceSpan
 */
constructor(
public switchValue: string,
public type: string,
public cases: ExpansionCase[],
public sourceSpan: ParseSourceSpan,
public switchValueSourceSpan: ParseSourceSpan) {}
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: Visitor, context: any): any { return visitor.visitExpansion(this, context); }
}

function Expansion_tsickle_Closure_declarations() {
 /** @type {?} */
Expansion.prototype.switchValue;
 /** @type {?} */
Expansion.prototype.type;
 /** @type {?} */
Expansion.prototype.cases;
 /** @type {?} */
Expansion.prototype.sourceSpan;
 /** @type {?} */
Expansion.prototype.switchValueSourceSpan;
}


export class ExpansionCase implements Node {
/**
 * @param {?} value
 * @param {?} expression
 * @param {?} sourceSpan
 * @param {?} valueSourceSpan
 * @param {?} expSourceSpan
 */
constructor(
public value: string,
public expression: Node[],
public sourceSpan: ParseSourceSpan,
public valueSourceSpan: ParseSourceSpan,
public expSourceSpan: ParseSourceSpan) {}
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: Visitor, context: any): any { return visitor.visitExpansionCase(this, context); }
}

function ExpansionCase_tsickle_Closure_declarations() {
 /** @type {?} */
ExpansionCase.prototype.value;
 /** @type {?} */
ExpansionCase.prototype.expression;
 /** @type {?} */
ExpansionCase.prototype.sourceSpan;
 /** @type {?} */
ExpansionCase.prototype.valueSourceSpan;
 /** @type {?} */
ExpansionCase.prototype.expSourceSpan;
}


export class Attribute implements Node {
/**
 * @param {?} name
 * @param {?} value
 * @param {?} sourceSpan
 * @param {?=} valueSpan
 */
constructor(
public name: string,
public value: string,
public sourceSpan: ParseSourceSpan,
public valueSpan?: ParseSourceSpan) {}
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: Visitor, context: any): any { return visitor.visitAttribute(this, context); }
}

function Attribute_tsickle_Closure_declarations() {
 /** @type {?} */
Attribute.prototype.name;
 /** @type {?} */
Attribute.prototype.value;
 /** @type {?} */
Attribute.prototype.sourceSpan;
 /** @type {?} */
Attribute.prototype.valueSpan;
}


export class Element implements Node {
/**
 * @param {?} name
 * @param {?} attrs
 * @param {?} children
 * @param {?} sourceSpan
 * @param {?} startSourceSpan
 * @param {?} endSourceSpan
 */
constructor(
public name: string,
public attrs: Attribute[],
public children: Node[],
public sourceSpan: ParseSourceSpan,
public startSourceSpan: ParseSourceSpan,
public endSourceSpan: ParseSourceSpan) {}
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: Visitor, context: any): any { return visitor.visitElement(this, context); }
}

function Element_tsickle_Closure_declarations() {
 /** @type {?} */
Element.prototype.name;
 /** @type {?} */
Element.prototype.attrs;
 /** @type {?} */
Element.prototype.children;
 /** @type {?} */
Element.prototype.sourceSpan;
 /** @type {?} */
Element.prototype.startSourceSpan;
 /** @type {?} */
Element.prototype.endSourceSpan;
}


export class Comment implements Node {
/**
 * @param {?} value
 * @param {?} sourceSpan
 */
constructor(public value: string,
public sourceSpan: ParseSourceSpan) {}
/**
 * @param {?} visitor
 * @param {?} context
 * @return {?}
 */
visit(visitor: Visitor, context: any): any { return visitor.visitComment(this, context); }
}

function Comment_tsickle_Closure_declarations() {
 /** @type {?} */
Comment.prototype.value;
 /** @type {?} */
Comment.prototype.sourceSpan;
}


export interface Visitor {
  // Returning a truthy value from `visit()` will prevent `visitAll()` from the call to the typed
  // method and result returned will become the result included in `visitAll()`s result array.
  visit?(node: Node, context: any): any;

  visitElement(element: Element, context: any): any;
  visitAttribute(attribute: Attribute, context: any): any;
  visitText(text: Text, context: any): any;
  visitComment(comment: Comment, context: any): any;
  visitExpansion(expansion: Expansion, context: any): any;
  visitExpansionCase(expansionCase: ExpansionCase, context: any): any;
}
/**
 * @param {?} visitor
 * @param {?} nodes
 * @param {?=} context
 * @return {?}
 */
export function visitAll(visitor: Visitor, nodes: Node[], context: any = null): any[] {
  const /** @type {?} */ result: any[] = [];

  const /** @type {?} */ visit = visitor.visit ?
      (ast: Node) => visitor.visit(ast, context) || ast.visit(visitor, context) :
      (ast: Node) => ast.visit(visitor, context);
  nodes.forEach(ast => {
    const /** @type {?} */ astResult = visit(ast);
    if (astResult) {
      result.push(astResult);
    }
  });
  return result;
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
